import moment from "moment";
import React, {useState} from "react";
import {faSearch, faSyncAlt} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useForm} from "react-hook-form";
import cn from "classnames";
import ReactTable from "react-table";
import "react-table/react-table.css";
import * as api from "../../api/IrsClient";
import MONTH from "../../resources/data/month.json";
import Modal from "../common/Modal";
import ModalNotFound from "../common/ModalNotFound";
import {BASE_10} from "../../constant";

const BotReportDownload = () => {
  const { register, handleSubmit, errors, reset } = useForm({
    mode: "onChange",
  });
  const [botReport, setBotReport] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchDate, setSearchDate] = useState("");
  const [isFailShown, setIsFailShown] = useState(false);
  const [notFound, setNotfound] = useState(null);

  async function getBotReport(values) {
    try {
      setLoading(true);
      const {
        data: {
          data,
          status: { code },
        },
      } = await api.getBotReport(values);
      setLoading(false);
      if (data.originals.length === 0 && data.modified.length === 0 && data.regenerated.length === 0) {
        reset();
        setNotfound({ title: "" });
      }
      if (code !== "0") return;
      setBotReport(formatBotReport(data));
    } catch (error) {
      setLoading(false);
      console.log("Error while call getBotReport()", error);
    }
  }

  async function download(file) {
    try {
      const type = file.type.charAt(0).toUpperCase();
      setLoading(true);
      const res = await api.downloadBotReport({
        date: searchDate,
        filename: file.file_name,
        report_type: type === "M" ? "V" : type,
        user_create: file.create_by,
      });

      const contentDisposition = res.headers['content-disposition']
      const filename = generateFilename(contentDisposition);
      
      setLoading(false);
      if (res.data.status) {
        setIsFailShown(true);
        return;
      }
      const blob = new Blob([res.data]);
      const href = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setLoading(false);
      console.log("Error while call download()", error);
    }
  }

  function generateFilename(contentDisposition) {
    return contentDisposition.replace(/attachment;filename=/gi, "")
  }

  function clear(e) {
    e.preventDefault();
    reset();
    setBotReport([]);
  }

  function formatBotReport(data) {
    const formattedBotReport = [];
    for (const [key, value] of Object.entries(data)) {
      formattedBotReport.push(
        ...value.map((item) => ({
          ...item,
          date: moment(item.date, "DD/MM/YYYY  HH:mm:ss").valueOf(),
          type: key,
        }))
      );
    }
    return formattedBotReport;
  }

  function onSubmit(values) {
    setSearchDate(values.year + values.month);
    getBotReport(values);
  }

  function prepareColumns() {
    return [
      {
        Header: "Date Time",
        accessor: "date",
        Cell: ({ value }) => {
          return moment(value).format("DD/MM/YYYY  HH:mm:ss");
        },
      },
      {
        Header: "Type",
        accessor: "type",
      },
      {
        Header: "File Name",
        accessor: "file_name",
        width: 400,
        style: { textAlign: "left" },
      },
      {
        Header: "File Uploaded By",
        accessor: "create_by",
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ original }) => {
          return (
            <button
              data-testid="btn-download-file"
              className="btn btn-download"
              onClick={() => download(original)}
              disabled={isLoading}
            >
              Download
            </button>
          );
        },
      },
    ];
  }

  const currentYear = parseInt(moment().format("YYYY"), BASE_10);

  return (
    <>
      <ModalNotFound
          message={"BOT report list not found."}
          isOpen={!!notFound}
          description={notFound?.description ?? ""}
          onRequestClose={() => setNotfound(null)}
      />
      <div className="container">
        <div className="card card-secondary">
          <div className="card-header">
            <h5>Download BOT Report</h5>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} data-testid="bot-download-form">
            <div className="p-3">
              <div className="row">
                <div className="col-sm-4">
                  <div
                    className={cn("form-group row", {
                      "form-group-error": errors?.month,
                    })}
                  >
                    <label className="col-form-label col-sm-4 text-lg-right">
                      Month
                    </label>
                    <div className="col-sm-8 p-0">
                      <select data-testid="month"
                        className="form-control"
                        name="month"
                        {...register('month' ,{ required: true })}
                      >
                        <option value="">Select...</option>
                        {MONTH.map((x) => (
                          <option key={x.value} value={x.value}>
                            {x.name}
                          </option>
                        ))}
                      </select>
                      {errors?.month?.type === "required" && (
                        <span className="input-error-message">
                          Please select month
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-sm-4">
                  <div
                    className={cn("form-group row", {
                      "form-group-error": errors?.year,
                    })}
                  >
                    <label className="col-form-label col-sm-4 text-lg-right">
                      Year
                    </label>
                    <div className="col-sm-8 p-0">
                      <select data-testid="year"
                        className="form-control"
                        name="year"
                        {...register('year', { required: true })}
                      >
                        <option value="">Select...</option>
                        {Array.from(Array(currentYear - 2020)).map((_, i) => {
                          const year = currentYear - i;
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                      {errors?.year?.type === "required" && (
                        <span className="input-error-message">
                          Please select year
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-sm-4">
                  <button
                    className="btn btn-warning"
                    style={{ marginRight: 16 }}
                    type="submit"
                  >
                    <FontAwesomeIcon icon={faSearch} />
                    Search
                  </button>
                  <button
                    data-testid="bot-clear-report"
                    className="btn btn-secondary"
                    onClick={clear}
                  >
                    <FontAwesomeIcon icon={faSyncAlt} />
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </form>

          <div className="p-3">
            <ReactTable
              showPaginationTop
              data={botReport}
              columns={prepareColumns()}
              className="-highlight report"
              showPageJump={false}
              defaultSorted={[
                {
                  id: "date",
                  desc: true,
                },
              ]}
            />
          </div>
        </div>
      </div>

      <Modal
        loading={isLoading}
        message="Download fail"
        isOpen={isFailShown}
        onCancel={null}
        onOk={() => setIsFailShown(false)}
        textOk="Close"
      />
    </>
  );
};

export default BotReportDownload;
